import * as Sentry from "@sentry/react";

const domPath = (node: Node) => {
  const parts: string[] = [];
  for (
    let el = node instanceof Element ? node : node.parentElement;
    el && parts.length < 4;
    el = el.parentElement
  ) {
    const classes = typeof el.className === "string" ? el.className.split(" ").slice(0, 2) : [];
    parts.unshift([el.tagName.toLowerCase(), ...classes.filter(Boolean)].join("."));
  }
  return parts.join(" > ");
};

const reportedMovedNodes = new Set<string>();
const reportMovedNode = (message: string, parent: Node, node: Node) => {
  if (reportedMovedNodes.has(message)) return;
  reportedMovedNodes.add(message);
  Sentry.captureMessage(message, {
    level: "warning",
    tags: {
      module: "dom-patch",
      translated: /translated-(ltr|rtl)/.test(document.documentElement.className) ? "yes" : "no",
    },
    extra: {
      node: `${node.nodeName} ${JSON.stringify((node.textContent ?? "").slice(0, 50))}`,
      parent: domPath(parent),
      nodeConnected: node.isConnected,
      nodeCurrentParent: node.parentNode?.nodeName ?? null,
    },
  });
};

// Chrome translate and extensions move React's text nodes; skip the op instead of crashing (facebook/react#11538).
export const installDomPatch = () => {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportMovedNode("[DOM] removeChild of a node moved by a third party", this, child);
      return child;
    }
    originalRemoveChild.call(this, child);
    return child;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    node: T,
    child: Node | null
  ): T {
    if (child && child.parentNode !== this) {
      reportMovedNode("[DOM] insertBefore a node moved by a third party", this, child);
      return node;
    }
    originalInsertBefore.call(this, node, child);
    return node;
  };
};
